import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { interestedPartiesApi } from '../services/api';
import { useToast } from '../hooks/useToast';
import { PageHeader } from '../components/PageHeader';
import { DocumentManager } from '../components/DocumentManager';
import { RaciAssignments } from '../components/RaciAssignments';
import { LinkedArtefacts } from '../components/LinkedArtefacts';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type CIAItem = 'Confidentiality' | 'Integrity' | 'Availability';

type InterestedParty = {
  partyId: string;
  name: string;
  category?: string;
  partyType?: string;
  requirementDescription?: string;
  requirementType?: string;
  sourceReference?: string;
  relevantToIsms?: string;
  ciaImpact?: CIAItem[];
  owner?: string;
  monitoringMethod?: string;
  reviewFrequency?: string;
  complianceStatus?: string;
  lastReviewDate?: string;
  nextReviewDate?: string;
  notes?: string;
};

type FormState = Omit<InterestedParty, 'partyId'>;

const EMPTY_FORM: FormState = {
  name: '',
  category: '',
  partyType: 'External',
  requirementDescription: '',
  requirementType: '',
  sourceReference: '',
  relevantToIsms: 'Yes',
  ciaImpact: [],
  owner: '',
  monitoringMethod: '',
  reviewFrequency: 'Annual',
  complianceStatus: 'Not Assessed',
  lastReviewDate: '',
  nextReviewDate: '',
  notes: '',
};

// ---------------------------------------------------------------------------
// Option lists
// ---------------------------------------------------------------------------

const CATEGORIES = [
  'Regulator', 'Customer', 'Supplier', 'Partner', 'Employee',
  'Shareholder', 'Government', 'Community', 'Board / Governance', 'Other',
];

const REQUIREMENT_TYPES = [
  'Legal', 'Regulatory', 'Contractual', 'Voluntary', 'Industry Standard', 'Internal Policy',
];

const REVIEW_FREQUENCIES = ['Monthly', 'Quarterly', 'Semi-Annual', 'Annual', 'Ad-hoc'];

const COMPLIANCE_STATUSES = [
  'Compliant', 'Partially Compliant', 'Non-Compliant', 'Under Review', 'Not Assessed',
];

const CIA_OPTIONS: CIAItem[] = ['Confidentiality', 'Integrity', 'Availability'];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const InterestedPartyFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();

  const isEdit = !!id && id !== 'new';

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [activeTab, setActiveTab] = useState<'details' | 'attachments' | 'raci' | 'artefacts'>('details');

  // ── Fetch existing party ────────────────────────────────────────────────
  const { data: partyData, isLoading: partyLoading } = useQuery<any>({
    queryKey: ['interestedParty', id],
    queryFn: () => interestedPartiesApi.getOne(id!),
    enabled: isEdit,
  });

  useEffect(() => {
    if (!partyData) return;
    const p: InterestedParty = partyData.interestedParty ?? partyData;
    setForm({
      name: p.name || '',
      category: p.category || '',
      partyType: p.partyType || 'External',
      requirementDescription: p.requirementDescription || '',
      requirementType: p.requirementType || '',
      sourceReference: p.sourceReference || '',
      relevantToIsms: p.relevantToIsms || 'Yes',
      ciaImpact: p.ciaImpact || [],
      owner: p.owner || '',
      monitoringMethod: p.monitoringMethod || '',
      reviewFrequency: p.reviewFrequency || 'Annual',
      complianceStatus: p.complianceStatus || 'Not Assessed',
      lastReviewDate: p.lastReviewDate || '',
      nextReviewDate: p.nextReviewDate || '',
      notes: p.notes || '',
    });
  }, [partyData]);

  // ── Helpers ─────────────────────────────────────────────────────────────
  const setField = (key: keyof FormState, value: any) =>
    setForm((f) => ({ ...f, [key]: value }));

  const toggleCIA = (item: CIAItem) => {
    setForm((f) => ({
      ...f,
      ciaImpact: f.ciaImpact?.includes(item)
        ? f.ciaImpact.filter((c) => c !== item)
        : [...(f.ciaImpact || []), item],
    }));
  };

  // ── Mutations ───────────────────────────────────────────────────────────
  const saveMutation = useMutation({
    mutationFn: async () => {
      if (isEdit) {
        await interestedPartiesApi.update(id!, form);
        return null;
      } else {
        const result = await interestedPartiesApi.create(form);
        return result;
      }
    },
    onSuccess: async (result) => {
      await qc.invalidateQueries({ queryKey: ['interested-parties'] });
      if (!isEdit && result) {
        const newId = result.interestedParty?.partyId ?? result.partyId;
        toast.success('Interested party created.');
        if (newId) {
          navigate(`/interested-parties/${newId}/edit`, { replace: true });
          return;
        }
        navigate('/interested-parties');
        return;
      }
      toast.success(isEdit ? 'Interested party updated.' : 'Interested party created.');
    },
    onError: (e: Error) => toast.error(`Failed to save: ${e.message}`),
  });

  // ── Render ──────────────────────────────────────────────────────────────
  const partyId = isEdit ? id : null;

  if (isEdit && partyLoading) {
    return (
      <div className="container-xxl flex-grow-1 container-p-y d-flex justify-content-center align-items-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">
      <PageHeader
        icon="ri-group-line"
        title={isEdit ? (form.name || 'Edit Interested Party') : 'New Interested Party'}
        colour="primary"
      />

      {isEdit && (
        <div className="mb-3 d-flex align-items-center gap-2">
          <span className="np-chip np-tone-primary np-tabular">{id}</span>
          <span className="np-sub">{form.name}</span>
        </div>
      )}

      {/* Tab navigation */}
      <div className="np-tabs">
        <button
          type="button"
          className={`np-tab d-flex align-items-center gap-1${activeTab === 'details' ? ' active' : ''}`}
          onClick={() => setActiveTab('details')}
        >
          <i className="ri-file-list-3-line"></i>Details
        </button>
        <button
          type="button"
          className={`np-tab d-flex align-items-center gap-1${activeTab === 'attachments' ? ' active' : ''}`}
          onClick={() => setActiveTab('attachments')}
        >
          <i className="ri-attachment-2"></i>Attachments
        </button>
        <button
          type="button"
          className={`np-tab d-flex align-items-center gap-1${activeTab === 'raci' ? ' active' : ''}`}
          onClick={() => setActiveTab('raci')}
        >
          <i className="ri-team-line"></i>RACI
        </button>
        <button
          type="button"
          className={`np-tab d-flex align-items-center gap-1${activeTab === 'artefacts' ? ' active' : ''}`}
          onClick={() => setActiveTab('artefacts')}
        >
          <i className="ri-links-line"></i>Linked Artefacts
        </button>
      </div>

      {/* ── Details tab ────────────────────────────────────────── */}
      {activeTab === 'details' && (
        <>
          {/* Party Details section */}
          <div className="np-card mb-4">
            <div className="np-card-head">
              <h2><i className="ri-group-line text-primary"></i> Party Details</h2>
            </div>
            <div className="np-card-body">
              <div className="row g-3">
                <div className="col-md-5 np-field">
                  <label>Interested Party <span className="text-danger">*</span></label>
                  <input
                    className="np-input"
                    value={form.name}
                    onChange={(e) => setField('name', e.target.value)}
                    placeholder="e.g. Data Protection Authority"
                  />
                </div>
                <div className="col-md-4 np-field">
                  <label>Category</label>
                  <select className="np-select" value={form.category} onChange={(e) => setField('category', e.target.value)}>
                    <option value="">— Select category —</option>
                    {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div className="col-md-3 np-field">
                  <label>Internal / External</label>
                  <div className="np-pill-toggle-group">
                    {['Internal', 'External'].map((t) => (
                      <button
                        key={t}
                        type="button"
                        className={`np-pill-toggle np-tone-primary${form.partyType === t ? ' active' : ''}`}
                        onClick={() => setField('partyType', t)}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="col-12 np-field">
                  <label>Requirement Description</label>
                  <textarea
                    className="np-input"
                    rows={2}
                    value={form.requirementDescription}
                    onChange={(e) => setField('requirementDescription', e.target.value)}
                    placeholder="What they require from you (information security related)"
                  />
                </div>

                <div className="col-md-4 np-field">
                  <label>Requirement Type</label>
                  <select className="np-select" value={form.requirementType} onChange={(e) => setField('requirementType', e.target.value)}>
                    <option value="">— Select type —</option>
                    {REQUIREMENT_TYPES.map((t) => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div className="col-md-4 np-field">
                  <label>Source / Reference</label>
                  <input
                    className="np-input"
                    value={form.sourceReference}
                    onChange={(e) => setField('sourceReference', e.target.value)}
                    placeholder="e.g. GDPR Art. 5"
                  />
                </div>
                <div className="col-md-4 np-field">
                  <label>Relevant to ISMS?</label>
                  <select className="np-select" value={form.relevantToIsms} onChange={(e) => setField('relevantToIsms', e.target.value)}>
                    <option>Yes</option>
                    <option>No</option>
                    <option>Partial</option>
                  </select>
                </div>

                <div className="col-12 np-field">
                  <label>CIA Impact</label>
                  <div className="np-pill-toggle-group">
                    {CIA_OPTIONS.map((item) => {
                      const checked = form.ciaImpact?.includes(item) ?? false;
                      const tone = item === 'Confidentiality' ? 'primary' : item === 'Integrity' ? 'success' : 'warning';
                      return (
                        <button
                          key={item}
                          type="button"
                          className={`np-pill-toggle d-flex align-items-center gap-1 np-tone-${tone}${checked ? ' active' : ''}`}
                          onClick={() => toggleCIA(item)}
                        >
                          <i className={`ri-${checked ? 'checkbox-circle-fill' : 'checkbox-blank-circle-line'}`}></i>
                          {item}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Monitoring & Compliance section */}
          <div className="np-card mb-4">
            <div className="np-card-head">
              <h2><i className="ri-shield-check-line text-warning"></i> Monitoring &amp; Compliance</h2>
            </div>
            <div className="np-card-body">
              <div className="row g-3">
                <div className="col-md-6 np-field">
                  <label>Owner</label>
                  <input
                    className="np-input"
                    value={form.owner}
                    onChange={(e) => setField('owner', e.target.value)}
                    placeholder="e.g. Compliance Manager"
                  />
                </div>
                <div className="col-md-6 np-field">
                  <label>Monitoring Method</label>
                  <input
                    className="np-input"
                    value={form.monitoringMethod}
                    onChange={(e) => setField('monitoringMethod', e.target.value)}
                    placeholder="e.g. Annual compliance audit"
                  />
                </div>

                <div className="col-md-3 np-field">
                  <label>Review Frequency</label>
                  <select className="np-select" value={form.reviewFrequency} onChange={(e) => setField('reviewFrequency', e.target.value)}>
                    {REVIEW_FREQUENCIES.map((f) => <option key={f}>{f}</option>)}
                  </select>
                </div>
                <div className="col-md-3 np-field">
                  <label>Compliance Status</label>
                  <select className="np-select" value={form.complianceStatus} onChange={(e) => setField('complianceStatus', e.target.value)}>
                    {COMPLIANCE_STATUSES.map((s) => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div className="col-md-3 np-field">
                  <label>Last Review Date</label>
                  <input
                    type="date"
                    className="np-input"
                    value={form.lastReviewDate}
                    onChange={(e) => setField('lastReviewDate', e.target.value)}
                  />
                </div>
                <div className="col-md-3 np-field">
                  <label>Next Review Date</label>
                  <input
                    type="date"
                    className="np-input"
                    value={form.nextReviewDate}
                    onChange={(e) => setField('nextReviewDate', e.target.value)}
                  />
                </div>

                <div className="col-12 np-field">
                  <label>Notes</label>
                  <textarea
                    className="np-input"
                    rows={3}
                    value={form.notes}
                    onChange={(e) => setField('notes', e.target.value)}
                    placeholder="Additional notes or context"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Save / Cancel */}
          <div className="d-flex gap-2 justify-content-end">
            <button
              type="button"
              className="np-btn np-btn-ghost"
              onClick={() => navigate('/interested-parties')}
            >
              Cancel
            </button>
            <button
              type="button"
              className="np-btn np-btn-primary"
              disabled={!form.name.trim() || saveMutation.isPending}
              onClick={() => saveMutation.mutate()}
            >
              {saveMutation.isPending ? (
                <><span className="spinner-border spinner-border-sm me-1"></span>Saving…</>
              ) : isEdit ? (
                'Save Changes'
              ) : (
                'Add Party'
              )}
            </button>
          </div>
        </>
      )}

      {/* ── Attachments tab ────────────────────────────────────── */}
      {activeTab === 'attachments' && (
        <div className="row">
          {partyId ? (
            <DocumentManager entityId={partyId} entityType="interested_party" />
          ) : (
            <div className="col-12 mb-4">
              <div className="np-card">
                <div className="np-card-head">
                  <h2><i className="ri-attachment-2"></i> Attachments</h2>
                </div>
                <div className="np-card-body">
                  <p className="np-sub mb-0">Save this party first, then come back to attach documents.</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── RACI tab ───────────────────────────────────────────── */}
      {activeTab === 'raci' && (
        <div className="row">
          {partyId ? (
            <RaciAssignments artefactId={partyId} artefactType="interested_party" />
          ) : (
            <div className="col-12 mb-4">
              <div className="np-card">
                <div className="np-card-head">
                  <h2><i className="ri-team-line"></i> RACI Assignments</h2>
                </div>
                <div className="np-card-body">
                  <p className="np-sub mb-0">Save this party first, then come back to assign RACI roles.</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Linked Artefacts tab ───────────────────────────────── */}
      {activeTab === 'artefacts' && (
        <div className="row">
          {partyId ? (
            <>
              <p className="np-sub mb-3">
                <i className="ri-information-line me-1"></i>
                Link risks, controls, documents, or other artefacts to this interested party.
              </p>
              <LinkedArtefacts
                artefactId={partyId}
                artefactType="interested_party"
                artefactRegister="interested-parties"
              />
            </>
          ) : (
            <div className="col-12 mb-4">
              <div className="np-card">
                <div className="np-card-head">
                  <h2><i className="ri-links-line"></i> Linked Artefacts</h2>
                </div>
                <div className="np-card-body">
                  <p className="np-sub mb-0">Save this party first, then come back to link artefacts.</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Back nav for non-details tabs */}
      {activeTab !== 'details' && (
        <div className="d-flex gap-2 justify-content-end">
          <button
            type="button"
            className="np-btn np-btn-ghost"
            onClick={() => navigate('/interested-parties')}
          >
            Back to Interested Parties
          </button>
        </div>
      )}
    </div>
  );
};
